// @erp/module-fin/fe · the query-key factory (DEV-GUIDE-002 §3.5 — ad-hoc
// string keys are BANNED). Hierarchical tuples rooted at ['fin'], one branch
// per routed domain; prefix-invalidation sweeps a domain's lists AND details.
// Filter params stay GENERIC (<F>) — the concrete schema query type flows in at
// the call site (the cms keys precedent).

/** The one root. Everything below composes from it; `all` alone invalidates the module. */
const ROOT = ["fin"] as const;

export const finKeys = {
  all: ROOT,

  accounts: {
    all: () => [...ROOT, "accounts"] as const,
    list: () => [...ROOT, "accounts", "list"] as const,
  },

  // `stats()` (2026-09-22) keys the GET /<register>/stats figures the register's stat
  // tiles read — a leaf under the SAME domain prefix as the list, so every write that
  // sweeps invoices.all() / ledger.all() (create, void, verify, decline) refreshes the
  // figures with the page and no mutation has to name them. The accounts registry has
  // no stats leaf on purpose: its list IS the whole register (schemas/stats.ts).
  invoices: {
    all: () => [...ROOT, "invoices"] as const,
    list: <F>(filters: F) => [...ROOT, "invoices", "list", filters] as const,
    detail: (id: string) => [...ROOT, "invoices", "detail", id] as const,
    stats: () => [...ROOT, "invoices", "stats"] as const,
  },

  ledger: {
    all: () => [...ROOT, "ledger"] as const,
    list: <F>(filters: F) => [...ROOT, "ledger", "list", filters] as const,
    stats: () => [...ROOT, "ledger", "stats"] as const,
  },

  verification: {
    all: () => [...ROOT, "verification"] as const,
    list: <F>(filters: F) => [...ROOT, "verification", "list", filters] as const,
    stats: () => [...ROOT, "verification", "stats"] as const,
  },

  // The payer surface — its own branch so a payer action never sweeps the console caches.
  my: {
    all: () => [...ROOT, "my"] as const,
    invoices: <F>(filters: F) => [...ROOT, "my", "invoices", filters] as const,
    invoiceDetail: (id: string) => [...ROOT, "my", "invoices", "detail", id] as const,
    payments: <F>(filters: F) => [...ROOT, "my", "payments", filters] as const,
  },

  proofs: {
    serve: (id: string) => [...ROOT, "proofs", id] as const,
  },
} as const;

export type FinKeys = typeof finKeys;
